/**
 * Where the API lives, resolved when the app boots rather than when it is bundled.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, which means a bundle built for staging
 * physically contains staging's API URL and cannot be deployed anywhere else. That forced a
 * separate build per environment, and it meant production ran a bundle that had never been
 * exercised on staging — the opposite of what promoting a release is for.
 *
 * `public/config.js` is copied to the deployment root untouched by the bundler, so it is the
 * one file that can differ between environments while the hashed assets stay byte-identical.
 * Swap it at upload time and the same artifact serves dev, staging and production.
 *
 * Precedence is deliberate. The runtime value only wins when it is actually set, so a build
 * that relies on VITE_API_URL — local `vite dev`, mock mode, the e2e `--mode test` build —
 * behaves exactly as it did before. An unconfigured config.js is inert, not destructive.
 */

declare global {
  interface Window {
    __BIZCORE_CONFIG__?: { apiBase?: string };
  }
}

/** Matches the pre-existing fallback so local development is unchanged. */
const LOCAL_FALLBACK = 'http://localhost:5107/api';

function resolveApiBase(): string {
  const runtime = typeof window !== 'undefined' ? window.__BIZCORE_CONFIG__?.apiBase : undefined;
  if (runtime && runtime.trim() !== '') return runtime.trim();
  return import.meta.env.VITE_API_URL || LOCAL_FALLBACK;
}

/**
 * Read once at module load. The value cannot change during a session, and callers use it in
 * module initialisers (axios.create, and so on) that run before any component mounts.
 */
export const API_BASE = resolveApiBase();
